// Lightweight HTML pretty-printer for the email-template source editor.
// Splits between `>` and `<`, then re-indents based on tag depth. Handles void
// elements, self-closing tags, comments, doctype, and inline element pairs
// (`<p>x</p>`) so they don't blow the indent up unnecessarily.
//
// This is intentionally not a full HTML parser — emails are HTML with
// well-behaved structure, and a parser would balloon the bundle. If we ever
// need true correctness (script/style content preservation, attribute wrapping,
// etc.) swap to prettier/html-beautify.

const VOID_TAG = /^<(area|base|br|col|embed|hr|img|input|link|meta|param|source|track|wbr)\b/i;
const VOID_CLOSE_TAG = /\/\s*>$/;
const COMMENT = /^<!--/;
const COMMENT_END = /-->$/;
const DOCTYPE = /^<!doctype/i;
const PRESERVE_AS_IS_OPEN = /^<(script|style|pre|textarea)\b/i;
const PRESERVE_AS_IS_CLOSE_FOR = (tag: string) => new RegExp(`</${tag}\\s*>$`, "i");
const INLINE_CLOSE_AT_END = /<\/\w+>\s*$/;
const OPEN_TAG_NAME = /^<\s*([a-zA-Z][\w-]*)/;

export function formatHtml(html: string, indent = "  "): string {
  if (!html.trim()) return html;

  // Put each tag and each text-run on its own line. Splitting only between
  // adjacent `>` `<` leaves text-then-tag (`Hello<br>`) glued together; this
  // also injects a newline before and after every tag so void elements like
  // `<br>` and `<img>` cleanly start a new line even when text precedes them.
  const tokens = html
    .replace(/(<\/?[a-zA-Z!][^>]*\/?>)/g, "\n$1\n")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

  const result: string[] = [];
  let depth = 0;
  let preserveUntil: string | null = null;
  let preserveBuf: string[] = [];

  for (const token of tokens) {
    // Pass-through block: script/style/pre etc. keep their interior intact.
    if (preserveUntil) {
      preserveBuf.push(token);
      if (PRESERVE_AS_IS_CLOSE_FOR(preserveUntil).test(token)) {
        result.push(indent.repeat(depth) + preserveBuf.join(""));
        preserveBuf = [];
        preserveUntil = null;
      }
      continue;
    }

    const isTag = token.startsWith("<");
    const isClosing = token.startsWith("</");
    const isComment = COMMENT.test(token);
    const isDoctype = DOCTYPE.test(token);
    const isVoid = isTag && (VOID_TAG.test(token) || VOID_CLOSE_TAG.test(token));
    const isInlinePair = isTag && !isClosing && !isComment && INLINE_CLOSE_AT_END.test(token);

    if (isClosing) depth = Math.max(0, depth - 1);
    result.push(indent.repeat(depth) + token);

    if (PRESERVE_AS_IS_OPEN.test(token) && !isInlinePair) {
      const match = OPEN_TAG_NAME.exec(token);
      if (match) {
        preserveUntil = match[1];
        preserveBuf.push(token);
        // Pop the placeholder we just pushed; it'll be re-emitted as one line.
        result.pop();
      }
      continue;
    }

    if (
      isTag &&
      !isClosing &&
      !isComment &&
      !isDoctype &&
      !isVoid &&
      !isInlinePair &&
      !COMMENT_END.test(token) // safety
    ) {
      depth++;
    }
  }

  // Any unterminated preserve buffer ends up appended as-is.
  if (preserveBuf.length) {
    result.push(indent.repeat(depth) + preserveBuf.join(""));
  }

  return result.join("\n");
}
